import { create } from "zustand";
import type { Notification } from "../components/Notification";

interface NotificationStore {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, "id">) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Métodos de conveniencia para diferentes tipos
  showSuccess: (title: string, message?: string, options?: Partial<Notification>) => void;
  showError: (title: string, message?: string, options?: Partial<Notification>) => void;
  showWarning: (title: string, message?: string, options?: Partial<Notification>) => void;
  showInfo: (title: string, message?: string, options?: Partial<Notification>) => void;
}

const generateId = () => Math.random().toString(36).substr(2, 9);

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],

  addNotification: (notification) => {
    const id = generateId();
    const newNotification: Notification = {
      id,
      duration: 5000, // 5 segundos por defecto
      ...notification,
    };
    
    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove si tiene duración
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        get().removeNotification(id);
      }, newNotification.duration);
    }
  },

  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearNotifications: () => {
    set({ notifications: [] });
  },

  showSuccess: (title, message, options = {}) => {
    get().addNotification({
      type: "success",
      title,
      message,
      ...options,
    });
  },

  showError: (title, message, options = {}) => {
    get().addNotification({
      type: "error",
      title,
      message,
      duration: 7000, // Errores se muestran más tiempo
      ...options,
    });
  },

  showWarning: (title, message, options = {}) => {
    get().addNotification({
      type: "warning",
      title,
      message,
      duration: 6000,
      ...options,
    });
  },

  showInfo: (title, message, options = {}) => {
    get().addNotification({
      type: "info",
      title,
      message,
      ...options,
    });
  },
}));
